import { forwardRef, type CSSProperties, type ReactNode } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from '@vitness/icons';
import { cn, useTheme } from '@vitness/theme';

/** A single option in a {@link Select}. */
export interface SelectOption {
  /** Submitted value. */
  value: string;
  /** Visible label. Defaults to `value` when omitted. */
  label?: ReactNode;
  /** Disables this option. */
  disabled?: boolean;
}

/**
 * Public props for {@link Select}. Authored explicitly (rather than re-exporting
 * Radix's prop type) so the component's API surface stays independent of the
 * Radix version and never leaks Radix types to consumers.
 */
export interface SelectProps {
  /** Options to render. Use grouped data via repeated `Select` for now. */
  options: SelectOption[];
  /** Placeholder shown when no value is selected. */
  placeholder?: string;
  /** Controlled selected value. */
  value?: string;
  /** Uncontrolled initial value. */
  defaultValue?: string;
  /** Fires when the selection changes. */
  onValueChange?: (value: string) => void;
  /** Controlled open state of the popover. */
  open?: boolean;
  /** Fires when the popover open state changes. */
  onOpenChange?: (open: boolean) => void;
  /** Form field name. */
  name?: string;
  /** Whether a selection is required in a form. */
  required?: boolean;
  /** id applied to the trigger (wire up with {@link FormField}). */
  id?: string;
  /** `aria-describedby` forwarded to the trigger. */
  'aria-describedby'?: string;
  /** `aria-invalid` forwarded to the trigger. */
  'aria-invalid'?: boolean;
  /** `aria-label` for the trigger when no visible label is associated. */
  'aria-label'?: string;
  /** Class applied to the trigger. */
  className?: string;
  /** Disables the whole control. */
  disabled?: boolean;
}

/**
 * Accent-aware dropdown built on Radix Select — designed for filter use cases.
 * Renders a styled trigger plus a token-driven popover list with a check on the
 * active option. The focus ring and selected check use the active theme accent
 * via the `--vn-control-accent` CSS variable. Fully keyboard-accessible
 * (type-ahead, arrow navigation, Escape to close) and screen-reader-friendly.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    options,
    placeholder = 'Select…',
    id,
    className,
    disabled,
    'aria-describedby': describedBy,
    'aria-invalid': ariaInvalid,
    'aria-label': ariaLabel,
    ...rootProps
  },
  ref,
) {
  const { accentSafe } = useTheme();
  const accentStyle = { '--vn-control-accent': accentSafe } as CSSProperties;

  return (
    <SelectPrimitive.Root disabled={disabled} {...rootProps}>
      <SelectPrimitive.Trigger
        ref={ref}
        id={id}
        aria-describedby={describedBy}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        style={accentStyle}
        className={cn(
          'inline-flex h-[32px] w-full items-center justify-between gap-vn-2 rounded-vn-md px-vn-3',
          'border border-border-default bg-surface-input font-body text-[12px] text-text-primary',
          'outline-none transition-colors data-[placeholder]:text-text-muted',
          'focus-visible:border-[var(--vn-control-accent)] focus-visible:ring-2 focus-visible:ring-[var(--vn-control-accent)]',
          'disabled:cursor-not-allowed disabled:opacity-45',
          'aria-[invalid=true]:border-semantic-danger',
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon>
          <ChevronDown size={14} className="text-text-muted" aria-hidden="true" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          style={accentStyle}
          className={cn(
            'z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-vn-md',
            'border border-border-default bg-surface-card shadow-vn-card',
          )}
        >
          <SelectPrimitive.Viewport className="p-vn-1">
            {options.map(option => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'relative flex cursor-pointer select-none items-center rounded-vn-sm py-vn-1 pl-vn-2 pr-vn-6',
                  'font-body text-[12px] text-text-primary outline-none',
                  'data-[highlighted]:bg-surface-card-hover',
                  'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-45',
                )}
              >
                <SelectPrimitive.ItemText>
                  {option.label ?? option.value}
                </SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute right-vn-2 inline-flex items-center text-[var(--vn-control-accent)]">
                  <Check size={13} strokeWidth={3} aria-hidden="true" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
});
